import type { AccountSubType, GlAccount } from '../types/account';
import type { Item } from '../types/item';
import { defaultEquityAccountId } from './inventoryStartingValueForm';
import { daysAgoLocalIso, todayLocalIso } from './localDate';

export type AdjustmentPurpose = 'balance_sheet' | 'pnl';

export const ADJUSTMENT_PURPOSE_OPTIONS: { id: AdjustmentPurpose; label: string }[] = [
  { id: 'balance_sheet', label: 'Count correction (balance sheet)' },
  { id: 'pnl', label: 'Shrinkage / write-off (P&L)' },
];

const EXCLUDED_ADJUSTMENT_SUB_TYPES: AccountSubType[] = [
  'INVENTORY_ASSET',
  'CASH',
  'BANK',
  'ACCOUNTS_RECEIVABLE',
  'ACCOUNTS_PAYABLE',
  'INPUT_VAT',
  'VAT_PAYABLE',
  'COGS',
];

const isExcludedAdjustmentSubType = (account: GlAccount) =>
  EXCLUDED_ADJUSTMENT_SUB_TYPES.includes(account.account_sub_type);

export const isBalanceSheetOffsetAccount = (account: GlAccount) =>
  account.active &&
  !isExcludedAdjustmentSubType(account) &&
  (account.account_type === 'EQUITY' ||
    (account.account_type === 'ASSET' && account.account_sub_type === 'OTHER'));

export const isPnlOffsetAccount = (account: GlAccount) =>
  account.active && account.account_type === 'EXPENSE' && !isExcludedAdjustmentSubType(account);

const sortAccountsByNumber = (accounts: GlAccount[]) =>
  [...accounts].sort((a, b) => a.account_number.localeCompare(b.account_number, undefined, { numeric: true }));

export const filterAdjustmentAccountsByPurpose = (
  accounts: GlAccount[],
  purpose: AdjustmentPurpose
): GlAccount[] => {
  const filtered =
    purpose === 'balance_sheet'
      ? accounts.filter(isBalanceSheetOffsetAccount)
      : accounts.filter(isPnlOffsetAccount);
  return sortAccountsByNumber(filtered);
};

export const defaultAdjustmentAccountId = (
  accounts: GlAccount[],
  purpose: AdjustmentPurpose
): string => {
  if (purpose === 'balance_sheet') {
    return defaultEquityAccountId(accounts);
  }

  const eligible = filterAdjustmentAccountsByPurpose(accounts, 'pnl');
  const writeOff = eligible.find((a) => a.account_sub_type === 'WRITE_OFF');
  if (writeOff) {
    return String(writeOff.id);
  }
  const operatingExpense = eligible.find((a) => a.account_sub_type === 'OPERATING_EXPENSE');
  if (operatingExpense) {
    return String(operatingExpense.id);
  }
  return eligible.length > 0 ? String(eligible[0].id) : '';
};

export const formatAdjustmentAccountOption = (account: GlAccount) =>
  `${account.account_number} — ${account.name}`;

export interface AdjustmentLocalLine {
  item_id: number;
  item_name: string;
  item_sku: string;
  item_description: string;
  qty_on_hand: string;
  newQty: string;
}

export const parseAdjustmentQty = (value: string | number | undefined) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const qtyOnHandForLine = (line: AdjustmentLocalLine) =>
  parseAdjustmentQty(line.qty_on_hand);

export const newQtyForItem = (item: Item) => String(parseAdjustmentQty(item.qty_on_hand));

export const changeInQtyForLine = (line: AdjustmentLocalLine) => {
  if (!line.newQty.trim()) return null;
  const newQty = Number(line.newQty);
  if (!Number.isFinite(newQty)) return null;
  return newQty - qtyOnHandForLine(line);
};

export const todayIso = todayLocalIso;

export const daysAgoIso = daysAgoLocalIso;

export const formatAdjustmentLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const formatAdjustmentDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString();

export const formatAdjustmentTime = (dateString: string) =>
  new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export const canCreateAdjustment = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

export const canVoidAdjustment = () =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

export const getAdjustmentStatusColor = (status: string) => {
  switch (status.toUpperCase()) {
    case 'OPEN':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const emptyAdjustmentLine = (): AdjustmentLocalLine => ({
  item_id: 0,
  item_name: '',
  item_sku: '',
  item_description: '',
  qty_on_hand: '0',
  newQty: '',
});
